import React, { useContext, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { getDocs } from "firebase/firestore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { fmtUSD, daysSince } from "../../utils/reports/format";

/**
 * Open Purchase Orders — outstanding POs (quotes/orders not closed) with vendor,
 * type, status, item count, expected total, and age.
 */
export default function GenerateOpenPurchaseOrders({ user }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const ref = propertyCollection(selectedClient, selectedProperty, "PORs");
      const snap = await getDocs(ref);

      const open = snap.docs
        .map((d) => d.data())
        .filter((po) => po.isOpen !== false)
        .map((po) => ({
          vendor: po.vendor || "—",
          type: po.porType || "—",
          status: po.status || "—",
          itemCount: Array.isArray(po.items) ? po.items.length : 0,
          expected: Number(po.expectedTotal) || 0,
          age: daysSince(po.createdAt || po.quoteRequestedAt || po.date),
        }))
        .sort((a, b) => (b.age ?? -1) - (a.age ?? -1));

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "Open Purchase Orders",
        dateRange: `${open.length} open`,
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const total = open.reduce((s, p) => s + p.expected, 0);
      const body = open.map((p) => [
        p.vendor,
        p.type,
        p.status,
        String(p.itemCount),
        fmtUSD(p.expected),
        p.age == null ? "—" : String(p.age),
      ]);

      if (body.length === 0) {
        body.push([{ content: "No open purchase orders.", colSpan: 6, styles: { fontStyle: "italic" } }]);
      } else {
        body.push([
          { content: "TOTAL EXPECTED", colSpan: 4, styles: { fontStyle: "bold" } },
          { content: fmtUSD(total), styles: { fontStyle: "bold", halign: "right" } },
          { content: "", styles: {} },
        ]);
      }

      autoTable(pdf, {
        startY: contentTop,
        head: [["Vendor", "Type", "Status", "Items", "Expected", "Age (d)"]],
        body,
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: {
          3: { halign: "right", cellWidth: 16 },
          4: { halign: "right", cellWidth: 28 },
          5: { halign: "right", cellWidth: 18 },
        },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("Open_Purchase_Orders", { ext: "pdf" })
      );
    } catch (e) {
      console.error("Error generating Open Purchase Orders:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={generate}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={16} /> : null}
    >
      {loading ? "Generating..." : "Open Purchase Orders"}
    </Button>
  );
}
